import type { DateRangePickerValue } from '@lmn/common-search-widget/components/DateRangePicker/services/types';
import type { ParticipantsDropdownValue } from '@lmn/common-search-widget/components/ParticipantsDropdown/services/types';
import type { DynamicPackageFormValues } from '@lmn/common-search-widget/components/tabs/DynamicPackageTab/services/types';
import type { HotelFormValues } from '@lmn/common-search-widget/components/tabs/HotelTab/services/types';
import type { TrainFormValues } from '@lmn/common-search-widget/components/tabs/TrainTab/services/types';
import type { AutocompleteOption } from '@lmn/common-search-widget';
import { PlaceType } from '@lmn/common-search-widget/components/Autocompleter/services/types';
import { isPrompterDestinationOption } from '../guards';
import type { DefaultValuesCSW } from '../types';

const getParticipants = ({ rooms }: ParticipantsDropdownValue) => ({
  rooms: rooms.map((room) => ({
    adultsCount: room.adultsCount,
    children: room.children,
  })),
});

const getDates = ({ mode, calendar, flexible }: DateRangePickerValue) => ({
  mode,
  calendar,
  flexible,
});

const getPlace = (place: AutocompleteOption | null) => {
  if (!place) return null;
  if (!isPrompterDestinationOption(place.option)) return { code: place.value };

  const shouldReplaceHotelWithCity = place.option?.type === PlaceType.Hotel && place.option?.cityId;
  if (shouldReplaceHotelWithCity) return { code: place.option?.cityId as string, type: PlaceType.City };

  return {
    code: place.option.code,
    type: place.option.type,
  };
};

export const getHotelValues = ({ place, dates, participants }: HotelFormValues): DefaultValuesCSW['HOTEL'] => ({
  place: getPlace(place),
  dates: getDates(dates),
  participants: getParticipants(participants),
});

export const getDynamicPackageValues = ({
  class: flightClass,
  dates,
  fromPlace,
  participants,
  toPlace,
}: DynamicPackageFormValues): DefaultValuesCSW['DP'] => ({
  class: flightClass,
  dates: getDates(dates),
  fromPlace: getPlace(fromPlace),
  participants: getParticipants(participants),
  toPlace: getPlace(toPlace),
});

export const getTrainValues = ({
  class: trainClass,
  dates,
  fromPlace,
  participants,
  toPlace,
}: TrainFormValues): DefaultValuesCSW['TRAIN'] => ({
  class: trainClass,
  dates: getDates(dates),
  fromPlace: getPlace(fromPlace),
  participants: getParticipants(participants),
  toPlace: getPlace(toPlace),
});
